<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Filler,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

ChartJS.register(
  Title,
  Tooltip,
  Filler,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

const props = defineProps({
  data: {
    type: Array,
    default: () => new Array(24).fill(0),
  },
  color: {
    type: String,
    default: '#388E3C',
  },
  title: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: 'Conversas',
  },
});

const chartKey = ref(0);

const labels = Array.from({ length: 24 }, (_, i) => i + 'h');

// Detect dark mode reactively
const isDark = ref(document.body.classList.contains('dark'));
let darkModeObserver = null;

onMounted(() => {
  darkModeObserver = new MutationObserver(() => {
    isDark.value = document.body.classList.contains('dark');
    chartKey.value++;
  });
  darkModeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onBeforeUnmount(() => {
  if (darkModeObserver) darkModeObserver.disconnect();
});

const chartData = computed(() => {
  const dark = isDark.value;
  const lineColor = dark ? lightenColor(props.color, 40) : props.color;

  return {
    labels,
    datasets: [
      {
        label: props.label,
        data: props.data,
        borderColor: lineColor,
        backgroundColor: (ctx) => {
          if (!ctx.chart.chartArea) return 'transparent';
          const { top, bottom } = ctx.chart.chartArea;
          const gradient = ctx.chart.ctx.createLinearGradient(0, top, 0, bottom);
          const baseColor = hexToRgb(lineColor);
          gradient.addColorStop(0, `rgba(${baseColor}, 0.22)`);
          gradient.addColorStop(1, `rgba(${baseColor}, 0)`);
          return gradient;
        },
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: lineColor,
        pointHoverBorderColor: '#FFFFFF',
        pointHoverBorderWidth: 2,
      },
    ],
  };
});

const chartOptions = computed(() => {
  const dark = isDark.value;
  const gridColor = dark ? 'rgba(255,255,255,0.06)' : '#F3F4F6';
  const tickColor = dark ? '#9CA3AF' : '#6B7280';

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#000000',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 8,
        cornerRadius: 4,
        callbacks: {
          label: (ctx) => ' ' + ctx.parsed.y + ' conversa(s)',
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: tickColor,
          font: { size: 10, family: 'Inter, sans-serif' },
          maxTicksLimit: 8,
          autoSkip: true,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: tickColor,
          font: { size: 10, family: 'Inter, sans-serif' },
          maxTicksLimit: 6,
        },
        grid: {
          color: gridColor,
          lineWidth: 1,
        },
        border: { display: false },
      },
    },
  };
});

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r},${g},${b}`;
}

function lightenColor(hex, amount) {
  const h = hex.replace('#', '');
  let r = parseInt(h.substring(0, 2), 16);
  let g = parseInt(h.substring(2, 4), 16);
  let b = parseInt(h.substring(4, 6), 16);
  r = Math.min(255, r + amount);
  g = Math.min(255, g + amount);
  b = Math.min(255, b + amount);
  return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
}
</script>

<template>
  <div class="sz-line-chart-wrap">
    <Line
      :key="chartKey"
      :data="chartData"
      :options="chartOptions"
    />
  </div>
</template>

<style scoped>
.sz-line-chart-wrap {
  width: 100%;
  min-width: 0;
  flex: 1;
  height: 280px;
  position: relative;
}
</style>
